import React, { useEffect, useState, type ReactNode } from "react";
import {
  CHAN_KIND,
  CHAN_LABEL,
  CHAN_SHORT,
  fmtDecimal,
  fmtInt,
  type Insight,
  type MonthPoint,
  type StarStats,
  type ToneCount,
  type channelStats,
  type postRows,
  type topicRows,
} from "../sandbox/resumenData";
import { MobileScreen, MobileSheet } from "./MobileChrome";
import { KpiRail, type MobileKpi } from "./MobileCards";
import { RatingCardMobile } from "./RatingCardMobile";
import { PublicacionesMobile } from "./PublicacionesMobile";

export type { MobileKpi };

export interface MobileLocal {
  name: string;
  avg: number | null;
  reviews: number;
  lowPct: number;
  complaint: string | null;
  tag: { label: string; cls: string };
  id?: string;
}

export interface MobileAction {
  area: string;
  due: string;
  dueUrgent?: boolean;
  title: string;
  why: string;
  evidence: string;
  onAction: () => void;
}

interface Props {
  theme: "light" | "dark";
  pills: { id: string; label: string }[];
  platform: string;
  onPlatformChange: (id: string) => void;
  insights: Insight[];
  onInsight: (target: string) => void;
  kpiMessage: string | null;
  kpis: MobileKpi[];
  channels: ReturnType<typeof channelStats>;
  topics: ReturnType<typeof topicRows>;
  onOpenTema?: (key?: string) => void;
  rating: {
    title: string;
    months: MonthPoint[];
    stars: StarStats;
    starsCaption: string;
    whatIf: { moved: number; avg: number } | null;
  } | null;
  locals: MobileLocal[];
  onOpenLocal?: (id?: string) => void;
  posts: ReturnType<typeof postRows>;
  /** Abre la Bandeja en un comentario de una publicación. */
  onOpenComment?: (feedbackId: string) => void;
  actions: MobileAction[];
}

type Screen = "canales" | "nota" | "posts" | "plan";

const SCREEN_TITLE: Record<Screen, string> = {
  canales: "Canales",
  nota: "Nota en Google",
  posts: "Publicaciones",
  plan: "Plan de acción",
};

function Tone({ tone }: { tone: ToneCount }) {
  const classified = tone.pos + tone.neu + tone.neg;
  const w = (n: number) => `${classified > 0 ? ((n / classified) * 100).toFixed(1) : 0}%`;
  return (
    <div className="sbx-tonebar rz-tone" role="img" aria-label={`${tone.pos} positivos, ${tone.neu} neutros, ${tone.neg} negativos`}>
      <i className="pos" style={{ width: w(tone.pos) }} />
      <i className="neu" style={{ width: w(tone.neu) }} />
      <i className="neg" style={{ width: w(tone.neg) }} />
    </div>
  );
}

function NavRow({ label, meta, badge, onClick }: { label: string; meta?: string; badge?: number; onClick: () => void }) {
  return (
    <li>
      <button type="button" className="rz-row tap" onClick={onClick}>
        <div className="main">
          <strong>{label}</strong>
          {meta && <span className="meta">{meta}</span>}
        </div>
        {badge ? <em className="rz-count">{badge}</em> : null}
        <span className="chev" aria-hidden="true">
          ›
        </span>
      </button>
    </li>
  );
}

const plain = (i: Insight) => i.segs.map((s) => s.text).join("");
const plural = (n: number, one: string, many: string) => `${fmtInt(n)} ${n === 1 ? one : many}`;

/** Resumen de Social para el teléfono: lo esencial arriba y cada tema abre su propia pantalla. */
export function ResumenMobile(p: Props) {
  const [sheet, setSheet] = useState(false);
  const [screen, setScreen] = useState<Screen | null>(null);
  const [openPostId, setOpenPostId] = useState<string | null>(null);
  const openPost = p.posts.find((x) => x.id === openPostId) ?? null;

  useEffect(() => {
    const go = (e: Event) => {
      const id = (e as CustomEvent<string>).detail;
      if (id === "temas") p.onOpenTema?.();
      else if (id === "locales") p.onOpenLocal?.();
      else if (id === "plan") setScreen("plan");
      else if (id === "publicaciones") setScreen("posts");
      else if (id === "nota") setScreen("nota");
      else setScreen("canales");
    };
    window.addEventListener("sbx-goto", go);
    return () => window.removeEventListener("sbx-goto", go);
  });

  const lead = p.insights[0];
  const pending = p.channels.reduce((n, c) => n + c.negPending, 0);
  const attention = p.locals.filter((l) => l.tag.cls === "bad").length;
  const topTopic = p.topics.rows[0];

  let body: ReactNode = null;
  if (screen === "canales") {
    body = (
      <ul className="rz-list">
        {p.channels.map((c) => (
          <li key={c.chan} className="rz-row">
            <div className="main">
              <strong>{CHAN_LABEL[c.chan]}</strong>
              <span className="meta">
                {CHAN_KIND[c.chan]}
                {c.avg !== null ? ` · Nota ${fmtDecimal(c.avg)}` : ""}
              </span>
              <Tone tone={c.tone} />
              {c.topTopic && <span className="meta">Tema principal: {c.topTopic}</span>}
            </div>
            <div className="side">
              <strong>{fmtInt(c.total)}</strong>
              <span className={c.negPending > 0 ? "bad" : undefined}>
                {c.negPending > 0 ? `${fmtInt(c.negPending)} sin responder` : "Al día"}
              </span>
            </div>
          </li>
        ))}
      </ul>
    );
  } else if (screen === "nota" && p.rating) {
    body = (
      <RatingCardMobile
        title={p.rating.title}
        months={p.rating.months}
        stars={p.rating.stars}
        starsCaption={p.rating.starsCaption}
        whatIf={p.rating.whatIf}
      />
    );
  } else if (screen === "posts") {
    body = (
      <ul className="rz-list">
        {p.posts.map((x) => (
          <li key={x.id}>
            <button type="button" className="rz-row tap" data-testid="post-fila" onClick={() => setOpenPostId(x.id)}>
              <div className="main">
                <strong className="clamp">{x.name}</strong>
                <span className="meta">
                  {CHAN_SHORT[x.chan]} · {x.paid ? "Anuncio" : "Orgánico"}
                </span>
                <Tone tone={x.tone} />
                {x.repeated && <span className="meta">{x.repeated}</span>}
              </div>
              <div className="side">
                <strong>{fmtInt(x.total)}</strong>
                <span className={x.pending > 0 ? "bad" : undefined}>
                  {x.pending > 0 ? `${fmtInt(x.pending)} sin responder` : "Al día"}
                </span>
              </div>
              <span className="chev" aria-hidden="true">
                ›
              </span>
            </button>
          </li>
        ))}
      </ul>
    );
  } else if (screen === "plan") {
    body =
      p.actions.length > 0 ? (
        <ul className="rz-list">
          {p.actions.map((a, i) => (
            <li key={i}>
              <button
                type="button"
                className="rz-row tap"
                onClick={() => {
                  setScreen(null);
                  a.onAction();
                }}
              >
                <div className="main">
                  <span className="meta">
                    {a.area} · <b className={a.dueUrgent ? "bad" : undefined}>{a.due}</b>
                  </span>
                  <strong>{a.title}</strong>
                  <span className="meta clamp">{a.why}</span>
                </div>
                <div className="side go">{a.evidence}</div>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="rz-empty">No hay acciones pendientes para este período.</p>
      );
  }

  return (
    <div className="rz">
      {p.pills.length > 0 && (
        <div className="rz-chips" role="group" aria-label="Filtrar por canal">
          {p.pills.map((c) => (
            <button key={c.id} type="button" aria-pressed={p.platform === c.id} onClick={() => p.onPlatformChange(c.id)}>
              {c.label}
            </button>
          ))}
        </div>
      )}

      {lead && (
        <button type="button" className={`rz-alert ${lead.tone}`} onClick={() => setSheet(true)}>
          <span className="mark" aria-hidden="true" />
          <span className="txt">
            <b>{lead.label}</b>
            <span>{plain(lead)}</span>
          </span>
          <span className="more">{p.insights.length > 1 ? `+${p.insights.length - 1}` : "Ver"}</span>
        </button>
      )}

      {p.kpiMessage ? <p className="rz-empty">{p.kpiMessage}</p> : <KpiRail kpis={p.kpis} />}

      <ul className="rz-list" aria-label="Detalle">
        {p.channels.length > 0 && (
          <NavRow
            label="Canales"
            meta={`${plural(p.channels.length, "canal", "canales")}${pending > 0 ? ` · ${fmtInt(pending)} sin responder` : ""}`}
            onClick={() => setScreen("canales")}
          />
        )}
        {p.rating && (
          <NavRow
            label="Nota en Google"
            meta={`${fmtDecimal(p.rating.stars.avg ?? p.rating.stars.sum / Math.max(1, p.rating.stars.total))} · ${plural(p.rating.stars.total, "reseña", "reseñas")}`}
            onClick={() => setScreen("nota")}
          />
        )}
        {p.topics.rows.length > 0 && p.onOpenTema && (
          <NavRow
            label="Temas"
            meta={topTopic ? `${topTopic.name} · ${plural(topTopic.total, "mención", "menciones")}` : undefined}
            onClick={() => p.onOpenTema?.()}
          />
        )}
        {p.locals.length > 0 && p.onOpenLocal && (
          <NavRow
            label="Locales"
            meta={`${plural(p.locals.length, "local", "locales")}${attention > 0 ? ` · ${attention} por revisar` : ""}`}
            onClick={() => p.onOpenLocal?.()}
          />
        )}
        {p.posts.length > 0 && (
          <NavRow label="Publicaciones" meta={plural(p.posts.length, "publicación", "publicaciones")} onClick={() => setScreen("posts")} />
        )}
        <NavRow label="Plan de acción" badge={p.actions.length || undefined} onClick={() => setScreen("plan")} />
      </ul>

      {screen && (
        <MobileScreen title={SCREEN_TITLE[screen]} theme={p.theme} onBack={() => setScreen(null)}>
          <div className="rz">{body}</div>
        </MobileScreen>
      )}

      {openPost && (
        <PublicacionesMobile
          post={openPost}
          theme={p.theme}
          onBack={() => setOpenPostId(null)}
          onOpenComment={(id) => {
            setOpenPostId(null);
            setScreen(null);
            p.onOpenComment?.(id);
          }}
        />
      )}

      <MobileSheet open={sheet} title="Alertas y oportunidades" onClose={() => setSheet(false)} theme={p.theme}>
        <ul className="rz-alerts">
          {p.insights.map((i) => (
            <li key={i.id} className={i.tone}>
              <b>{i.label}</b>
              <p>
                {i.segs.map((s, idx) => (s.bold ? <strong key={idx}>{s.text}</strong> : <span key={idx}>{s.text}</span>))}
              </p>
              <button
                type="button"
                onClick={() => {
                  setSheet(false);
                  p.onInsight(i.target);
                }}
              >
                {i.cta}
              </button>
            </li>
          ))}
        </ul>
      </MobileSheet>
    </div>
  );
}
